import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  AccessTimeOutlined,
  ArrowUpward,
  Block,
  CheckCircleOutline,
  EditOutlined,
  LockOpen,
  SettingsOutlined,
  ShareOutlined,
  Summarize,
} from "@mui/icons-material";
import { Tooltip, Typography } from "@mui/material";
import {
  type AuditLogEntryDto,
  getSubmissionAuditLogPortal,
} from "@rubi/api-generated";
import { AuditLogList, type AuditLogEntry } from "@rubi/ui";

type AuditLogRow = AuditLogEntryDto & {
  source_submission_id?: string;
  source_label?: string;
};

interface Props {
  submissionIds: string[];
  supplementalLabels?: Record<string, string>;
}

const ICON_SX = { fontSize: 10 };

// Stable empty default so an omitted `supplementalLabels` doesn't create a new
// object each render — that would change the fetch effect's identity and re-run
// it on every render.
const EMPTY_SUPPLEMENTAL_LABELS: Record<string, string> = {};

function logMetadata(entry: AuditLogEntryDto): Record<string, unknown> {
  const raw = entry.log_metadata;
  if (typeof raw === "object" && raw !== null && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  return {};
}

function formatActivityDate(isoString: string): string {
  return new Date(isoString).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function categorizeEntry(entry: AuditLogEntryDto): string {
  const meta = logMetadata(entry);
  const actionName = entry.action_name.toLowerCase();
  const hasStatusChange =
    meta["old_status"] !== undefined && meta["new_status"] !== undefined;

  if (actionName.includes("share")) return "SHARE";
  if (actionName.includes("note")) return "NOTE";
  if (hasStatusChange || actionName.includes("status")) return "STATUS";
  if (entry.action_type === "READ") return "TIMELINE";
  if (!entry.name) return "SYSTEM";
  return entry.action_type.toUpperCase();
}

function getStatusChangeTarget(entry: AuditLogEntryDto): string | null {
  const meta = logMetadata(entry);
  const rawNewStatus = meta["new_status"];
  if (typeof rawNewStatus === "string" && rawNewStatus.trim()) {
    return rawNewStatus.trim().toLowerCase();
  }

  const actionName = entry.action_name.toLowerCase();
  if (actionName.includes("approved")) return "approved";
  if (actionName.includes("rejected")) return "rejected";
  if (actionName.includes("escalated") || actionName.includes("escalate")) {
    return "escalated";
  }
  if (
    actionName.includes("follow-up") ||
    actionName.includes("follow up") ||
    actionName.includes("modification")
  ) {
    return "modification_requested";
  }

  return null;
}

type Visual = Pick<AuditLogEntry, "icon" | "iconVariant" | "label">;

function defaultVisual(category: string): Visual {
  if (category === "UPDATE") {
    return {
      icon: <EditOutlined sx={ICON_SX} />,
      iconVariant: "update",
      label: "UPDATE",
    };
  }
  if (category === "SHARE") {
    return {
      icon: <ShareOutlined sx={ICON_SX} />,
      iconVariant: "share",
      label: "SHARE",
    };
  }
  if (category === "SYSTEM") {
    return {
      icon: <SettingsOutlined sx={ICON_SX} />,
      iconVariant: "system",
      label: "SYSTEM",
    };
  }
  return {
    icon: <AccessTimeOutlined sx={ICON_SX} />,
    iconVariant: "timeline",
    label: category,
  };
}

function statusVisualFor(entry: AuditLogEntryDto): Visual {
  const targetStatus = getStatusChangeTarget(entry);
  const actionName = entry.action_name.toLowerCase();

  if (
    actionName.includes("reopen") ||
    targetStatus === "submitted" ||
    targetStatus === "resubmitted"
  ) {
    return {
      icon: <LockOpen sx={ICON_SX} />,
      iconVariant: "info",
      label: "REOPENED",
    };
  }

  if (targetStatus === "approved") {
    return {
      icon: <CheckCircleOutline sx={ICON_SX} />,
      iconVariant: "success",
      label: "APPROVED",
    };
  }

  if (targetStatus === "rejected") {
    return {
      icon: <Block sx={ICON_SX} />,
      iconVariant: "error",
      label: "REJECTED",
    };
  }

  if (targetStatus === "escalated") {
    return {
      icon: <ArrowUpward sx={ICON_SX} />,
      iconVariant: "secondary",
      label: "ESCALATED",
    };
  }

  if (targetStatus === "modification_requested") {
    return {
      icon: <Summarize sx={ICON_SX} />,
      iconVariant: "warning",
      label: "FOLLOW-UP",
    };
  }

  return {
    icon: <AccessTimeOutlined sx={ICON_SX} />,
    iconVariant: "status",
    label: "STATUS",
  };
}

function visualForEntry(entry: AuditLogEntryDto, category: string): Visual {
  if (category === "STATUS") return statusVisualFor(entry);
  return defaultVisual(category);
}

function shareRecipient(entry: AuditLogEntryDto): string | null {
  const email = logMetadata(entry)["share_email"];
  return typeof email === "string" && email.trim() ? email.trim() : null;
}

function metadataLine(entry: AuditLogEntryDto): string {
  return `${entry.name ?? "System"} • ${formatActivityDate(entry.time_created)}`;
}

/** For each entry id: last supplemental link/unlink kind strictly before that row's timestamp (chronological scan). */
function supplementalLinkKindBeforeEntry(
  entries: AuditLogRow[],
): Map<string, "link" | "unlink" | null> {
  const asc = [...entries].sort((a, b) => {
    const dt =
      new Date(a.time_created).getTime() - new Date(b.time_created).getTime();
    if (dt !== 0) return dt;
    return a.id.localeCompare(b.id);
  });
  const out = new Map<string, "link" | "unlink" | null>();
  let last: "link" | "unlink" | null = null;
  for (const e of asc) {
    out.set(e.id, last);
    const name = e.action_name.toLowerCase();
    const isUnlink = name.includes("unlink supplemental report");
    const isLink = name.includes("link supplemental report") && !isUnlink;
    if (isLink || isUnlink) {
      last = isUnlink ? "unlink" : "link";
    }
  }
  return out;
}

function supplementalAdornment(label: string): ReactNode {
  return (
    <Tooltip title={label}>
      <Typography
        variant="caption"
        sx={{
          color: "text.secondary",
          fontWeight: 500,
          whiteSpace: "nowrap",
        }}
      >
        · Supplemental
      </Typography>
    </Tooltip>
  );
}

export function SubmissionActivityTab({
  submissionIds,
  supplementalLabels = EMPTY_SUPPLEMENTAL_LABELS,
}: Props) {
  const [entries, setEntries] = useState<AuditLogRow[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submissionKey = useMemo(
    () =>
      Array.from(new Set(submissionIds.filter((id) => id.trim().length > 0)))
        .sort()
        .join("|"),
    [submissionIds],
  );

  const fetchActivity = useCallback(async () => {
    const uniqueSubmissionIds = submissionKey
      ? submissionKey.split("|").filter(Boolean)
      : [];
    if (uniqueSubmissionIds.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.all(
        uniqueSubmissionIds.map((submissionId) =>
          getSubmissionAuditLogPortal({
            path: { submission_id: submissionId },
            throwOnError: true,
          }),
        ),
      );
      const mergedEntries = results
        .flatMap((result, index) => {
          const sourceSubmissionId = uniqueSubmissionIds[index];
          const sourceLabel = supplementalLabels[sourceSubmissionId];
          const rows = result.data?.entries ?? [];
          return rows.map((entry) => ({
            ...entry,
            source_submission_id: sourceSubmissionId,
            source_label: sourceLabel,
          }));
        })
        .sort(
          (a, b) =>
            new Date(b.time_created).getTime() -
            new Date(a.time_created).getTime(),
        );

      setEntries(mergedEntries);
    } catch {
      setError("Failed to load activity. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [submissionKey, supplementalLabels]);

  useEffect(() => {
    void fetchActivity();
  }, [fetchActivity]);

  const linkKindBeforeByEntryId = useMemo(
    () =>
      entries?.length
        ? supplementalLinkKindBeforeEntry(entries)
        : new Map<string, "link" | "unlink" | null>(),
    [entries],
  );

  const listEntries: AuditLogEntry[] = useMemo(() => {
    if (!entries) return [];
    return entries.map((entry) => {
      const category = categorizeEntry(entry);
      const visual = visualForEntry(entry, category);

      const lastLinkBefore =
        entry.action_name === "View Submission"
          ? (linkKindBeforeByEntryId.get(entry.id) ?? null)
          : null;
      const shouldUseFullReportLabel =
        entry.action_name === "View Submission" && lastLinkBefore === "link";
      const shouldUseReportLabel =
        entry.action_name === "View Submission" && !shouldUseFullReportLabel;
      const recipient = category === "SHARE" ? shareRecipient(entry) : null;
      const primaryText = shouldUseFullReportLabel
        ? "View Full Report"
        : shouldUseReportLabel
          ? "View Report"
          : recipient
            ? `Shared report with ${recipient}`
            : entry.action_name;

      return {
        id: entry.id,
        timestamp: entry.time_created,
        primaryText,
        primaryAdornment: entry.source_label
          ? supplementalAdornment(entry.source_label)
          : undefined,
        secondaryText: metadataLine(entry),
        ...visual,
      };
    });
  }, [entries, linkKindBeforeByEntryId]);

  if (entries === null && !loading && !error) return null;

  return (
    <AuditLogList
      entries={listEntries}
      loading={loading}
      error={error}
      onRetry={() => void fetchActivity()}
      title="Activity & Audit Trail"
      emptyMessage="No activity recorded yet."
    />
  );
}
